import { sdk as imageSDK } from '@wix/image-kit';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import React from 'react';
import { Counter, CounterSize, Text } from 'wix-ui-tpa/cssVars';
import { SlotAddOnImage } from '../../../../../../utils/state/types';
import { AddonsDetailsDataHooks } from '../../datahooks';
import { classes, st } from './AddonCard.st.css';

const THUMBNAIL_SIZE = 80;

export const AddonCard = ({
  index,
  name,
  price,
  image,
  quantity,
  isSelected,
  maxQuantity,
  isDisabled,
  groupMaxAddOns,
  onAdd,
  onRemove,
  onQuantityChange,
}: {
  index: number;
  name: string;
  price?: string;
  image?: SlotAddOnImage | null;
  quantity: number;
  // Whether the add-on is present in selectedAddons — not quantity > 0, since
  // a deep-link-preselected add-on can be present with quantity 0.
  isSelected: boolean;
  maxQuantity: number;
  isDisabled: boolean;
  // The group's selection cap, distinct from maxQuantity (per-add-on).
  // Only read while isDisabled, which implies the group has a cap.
  groupMaxAddOns?: number;
  onAdd: () => void;
  onRemove: () => void;
  onQuantityChange: (quantity: number) => void;
}) => {
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();

  const hasQuantity = maxQuantity > 1;
  // The dashboard stores a WixMedia id; url is only a fallback.
  const imageUri = image?.id || image?.url;
  const imageUrl = imageUri
    ? imageSDK.getScaleToFillImageURL(
        imageUri,
        image.width ?? 0,
        image.height ?? 0,
        THUMBNAIL_SIZE,
        THUMBNAIL_SIZE,
      )
    : undefined;

  // Stepping below one removes the add-on rather than clamping at 1.
  const handleCounterChange = (value: string) => {
    // Counter forwards the raw input value, so clearing the field arrives as
    // ''. That's a mid-edit state, not a removal — only an explicit step down
    // to 0 should remove.
    if (value === '') {
      return;
    }
    const nextQuantity = Number(value);
    if (nextQuantity > 0) {
      onQuantityChange(nextQuantity);
    } else {
      onRemove();
    }
  };

  const renderControl = () => {
    if (!isSelected) {
      return (
        <>
          {/* Plain <button>, not wix-ui-tpa's Button (see AddonCard.st.css).
              type="button" since this sits inside the booking form. */}
          <button
            type="button"
            className={classes.addButton}
            data-hook={`${AddonsDetailsDataHooks.ADDON_ADD_BUTTON}-${index}`}
            disabled={isDisabled}
            onClick={onAdd}
          >
            {t('app.bookings-form.add-ons.add-button')}
          </button>
          {isDisabled && (
            <Text
              className={classes.disabledReason}
              data-hook={`${AddonsDetailsDataHooks.ADDON_DISABLED_REASON}-${index}`}
            >
              {groupMaxAddOns === 1
                ? t('app.bookings-form.add-ons.max-reached-singular')
                : t('app.bookings-form.add-ons.max-reached', {
                    maxQuantity: groupMaxAddOns,
                  })}
            </Text>
          )}
        </>
      );
    }

    if (hasQuantity) {
      return (
        <Counter
          className={classes.counter}
          data-hook={`${AddonsDetailsDataHooks.ADDON_QUANTITY}-${index}`}
          inputAriaLabel={t('app.bookings-form.add-ons.quantity.aria-label')}
          value={quantity}
          step={1}
          min={0}
          max={maxQuantity}
          size={CounterSize.xSmall}
          onChange={handleCounterChange}
        />
      );
    }

    return (
      <button
        type="button"
        className={classes.addedButton}
        data-hook={`${AddonsDetailsDataHooks.ADDON_ADDED_CHIP}-${index}`}
        aria-label={t('app.bookings-form.add-ons.remove-button.aria-label', {
          name,
        })}
        onClick={onRemove}
      >
        <span aria-hidden="true">&#10003;</span>
        {t('app.bookings-form.add-ons.added-button')}
      </button>
    );
  };

  return (
    <div
      className={st(classes.card, {
        isDisabled: isDisabled && !isSelected,
        isMobile,
      })}
      data-hook={`${AddonsDetailsDataHooks.ADDON_CARD}-${index}`}
    >
      {imageUrl && (
        <img
          className={classes.image}
          data-hook={`${AddonsDetailsDataHooks.ADDON_IMAGE}-${index}`}
          src={imageUrl}
          alt={image?.altText ?? ''}
        />
      )}
      <div className={classes.info}>
        <Text
          className={classes.name}
          data-hook={`${AddonsDetailsDataHooks.ADDON_NAME}-${index}`}
        >
          {name}
        </Text>
        {price && (
          <Text
            className={classes.price}
            data-hook={`${AddonsDetailsDataHooks.ADDON_PRICE}-${index}`}
          >
            {price}
          </Text>
        )}
      </div>
      <div className={classes.control}>{renderControl()}</div>
    </div>
  );
};
